/**
 * Mobile social-login helpers (dependency-free WebView authorization-code flow).
 *
 * Facebook, GitHub, LinkedIn and Microsoft allow OAuth inside a WebView; the
 * provider redirects back to the backend callback URL, which we intercept in the
 * WebView (before it actually loads) and hand the `code` to `POST /auth/social`
 * for a server-side exchange. Apple has no native SDK on Android, so it uses this
 * same WebView code flow there (iOS keeps the native `expo-apple-authentication`
 * sheet). Google forbids WebView OAuth — it requires `expo-auth-session` /
 * `expo-web-browser` (see docs/SOCIAL_LOGIN_PROVIDER_SETUP.md).
 */

import { ApiConfig } from '../config/ApiConfig';
import { SOCIAL_CLIENT_IDS } from '../config/socialConfig';

/**
 * Providers usable via the dependency-free WebView code flow. `apple` is included
 * for ANDROID only (iOS uses the native `expo-apple-authentication` sheet); the
 * SocialLoginButtons component decides which path to render per platform.
 */
export const WEBVIEW_PROVIDERS = ['microsoft', 'facebook', 'github', 'linkedin', 'apple'];

/** @param {string} provider @returns {string} public client id (empty if unset). */
export function getClientId(provider) {
    return SOCIAL_CLIENT_IDS[provider] || '';
}

/**
 * The per-provider redirect URI we intercept in the WebView —
 * `<api>/auth/<provider>/callback`, exactly what each provider has registered.
 * The WebView cancels navigation to it before it loads and hands the `code`
 * to `POST /auth/social` for the server-side exchange.
 * @param {string} provider
 * @returns {string}
 */
export function getRedirectUri(provider) {
    return `${ApiConfig.apiURL}/auth/${provider}/callback`;
}

/** Non-crypto correlation token for the round-trip. */
function makeState() {
    return `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

function toQuery(params) {
    return Object.keys(params)
        .filter((k) => params[k] !== undefined && params[k] !== null && params[k] !== '')
        .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
        .join('&');
}

/**
 * Build the provider authorize URL.
 *
 * The native WebView flow lets `state` default to a plain correlation token
 * ({@link makeState}) — it intercepts the redirect before the backend relay
 * page renders, so no opener-origin round-trip is needed. The web popup flow
 * ({@link ../utils/socialOAuthWeb}) passes a base64url `{o,n}` `stateOverride`
 * so the backend relay can decode the opener origin and `postMessage` the
 * result back (and so Facebook/GitHub route to the relay page at all).
 *
 * @param {string} provider
 * @param {string} [stateOverride] Explicit `state` value (web popup: base64url `{o,n}`).
 * @returns {{url:string, redirectUri:string, state:string, clientId:string}}
 */
export function buildAuthorizeUrl(provider, stateOverride) {
    const clientId = getClientId(provider);
    const redirectUri = getRedirectUri(provider);
    const state = stateOverride || makeState();
    let url;

    switch (provider) {
    case 'facebook':
        url = `https://www.facebook.com/v19.0/dialog/oauth?${toQuery({
            client_id: clientId, redirect_uri: redirectUri, state, response_type: 'code', scope: 'email,public_profile',
        })}`;
        break;
    case 'github':
        url = `https://github.com/login/oauth/authorize?${toQuery({
            client_id: clientId, redirect_uri: redirectUri, state, scope: 'read:user user:email',
        })}`;
        break;
    case 'linkedin':
        url = `https://www.linkedin.com/oauth/v2/authorization?${toQuery({
            response_type: 'code', client_id: clientId, redirect_uri: redirectUri, state, scope: 'openid profile email',
        })}`;
        break;
    case 'microsoft':
        url = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${toQuery({
            client_id: clientId, response_type: 'code', redirect_uri: redirectUri,
            scope: 'openid email profile', response_mode: 'query', state,
        })}`;
        break;
    case 'apple':
        // Android fallback (no native Apple SDK). `scope` is deliberately omitted:
        // Apple forces `response_mode=form_post` whenever name/email scope is
        // requested, and a form_post redirect cannot be intercepted from a WebView
        // navigation. Existing-account sign-in only needs the stable `sub` in the
        // exchanged id_token, so `response_mode=query` (interceptable) is enough.
        // `client_id` is the Apple *Services ID*; the backend swaps the returned
        // code for an id_token at appleid.apple.com/auth/token.
        url = `https://appleid.apple.com/auth/authorize?${toQuery({
            client_id: clientId, redirect_uri: redirectUri, response_type: 'code', response_mode: 'query', state,
        })}`;
        break;
    default:
        throw new Error(`Unsupported WebView provider: ${provider}`);
    }

    return { url, redirectUri, state, clientId };
}

/** Parse an intercepted redirect URL's query/fragment (no URLSearchParams dep). */
export function parseRedirect(urlStr) {
    const out = {};
    const parseInto = (str) => {
        (str || '').split('&').forEach((pair) => {
            if (!pair) {
                return;
            }
            const idx = pair.indexOf('=');
            const key = idx >= 0 ? pair.slice(0, idx) : pair;
            const val = idx >= 0 ? pair.slice(idx + 1) : '';

            try {
                out[decodeURIComponent(key)] = decodeURIComponent(val.replace(/\+/g, ' '));
            } catch (_e) {
                out[key] = val;
            }
        });
    };

    const qIdx = urlStr.indexOf('?');
    const hIdx = urlStr.indexOf('#');

    if (qIdx >= 0) {
        parseInto(urlStr.slice(qIdx + 1).split('#')[0]);
    }
    if (hIdx >= 0) {
        parseInto(urlStr.slice(hIdx + 1));
    }

    return {
        code: out.code || null,
        state: out.state || null,
        error: out.error || out.error_description || null,
    };
}
